import React from 'react';
import Icon from '../../../components/AppIcon';

const BehaviorFlagsSection = ({ behaviorFlags, integrityScore, computedMetrics }) => {
  if (!behaviorFlags && !computedMetrics) return null;

  const hasIssues = behaviorFlags && (
    behaviorFlags.earlyExit || behaviorFlags.abusiveLanguage ||
    behaviorFlags.offTopicCount > 0 || behaviorFlags.scorePenalty > 0 ||
    behaviorFlags.tabSwitchDetected || behaviorFlags.copyPasteDetected ||
    behaviorFlags.selectionAttemptCount > 0
  );

  const getIntegrityStyle = (score) => {
    if (score >= 90) return { label: 'Excellent', color: 'text-emerald-500', bg: 'bg-emerald-50', border: 'border-emerald-100' };
    if (score >= 70) return { label: 'Good', color: 'text-blue-500', bg: 'bg-blue-50', border: 'border-blue-100' };
    if (score >= 50) return { label: 'Fair', color: 'text-amber-500', bg: 'bg-amber-50', border: 'border-amber-100' };
    return { label: 'Poor', color: 'text-red-500', bg: 'bg-red-50', border: 'border-red-100' };
  };

  const intStyle = getIntegrityStyle(integrityScore || 100);

  return (
    <div className="mt-8 space-y-4">
      {/* Integrity Score + Computed Speech Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Integrity Score */}
        <div className={`${intStyle.bg} border ${intStyle.border} rounded-[2rem] p-5`}>
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-xl bg-white shadow-sm">
              <Icon name="Shield" size={16} className={intStyle.color} />
            </div>
            <div>
              <h3 className="text-xs font-black text-slate-900tracking-[0.15em]">Interview Integrity</h3>
              <p className="text-[10px] text-slate-400 font-bold">How well you engaged with the process</p>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <div className="relative w-16 h-16 shrink-0">
              <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
                <circle cx="50" cy="50" r="40" stroke="rgba(0,0,0,0.05)" strokeWidth="8" fill="none" className="stroke-slate-100" />
                <circle cx="50" cy="50" r="40" stroke="currentColor"
                  className={intStyle.color} strokeWidth="8" fill="none"
                  strokeDasharray={2 * Math.PI * 40}
                  strokeDashoffset={2 * Math.PI * 40 * (1 - (integrityScore || 100) / 100)}
                  strokeLinecap="round" />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-lg font-black text-slate-900">{integrityScore || 100}</span>
              </div>
            </div>
            <div>
              <span className={`text-sm font-black ${intStyle.color}`}>{intStyle.label}</span>
              {!hasIssues && <p className="text-[11px] text-slate-500 font-medium mt-0.5">No integrity issues detected</p>}
            </div>
          </div>
        </div>

        {/* Computed Speech Metrics */}
        {computedMetrics && computedMetrics.totalUserWords > 0 && (
          <div className="bg-white border border-slate-100 rounded-[2rem] p-5">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 rounded-xl bg-slate-900 text-white shadow-sm">
                <Icon name="BarChart3" size={16} />
              </div>
              <h3 className="text-xs font-black text-slate-900tracking-[0.15em]">Measured Speech Stats</h3>
            </div>

            <div className="grid grid-cols-2 gap-3">
              {[
                { label: 'Words Spoken', value: computedMetrics.totalUserWords.toLocaleString(), icon: 'MessageSquare' },
                { label: 'Speaking Rate', value: `${Math.round(computedMetrics.wordsPerMinute)} WPM`, icon: 'Gauge' },
                { label: 'Talk Ratio', value: `${Math.round(computedMetrics.talkRatio * 100)}%`, icon: 'Users' },
                { label: 'Filler Words', value: `${computedMetrics.fillerWordCount} (${(computedMetrics.fillerWordRate * 100).toFixed(1)}%)`, icon: 'Mic' },
              ].map((stat, i) => (
                <div key={i} className="bg-slate-50 rounded-xl p-2.5 border border-slate-100">
                  <div className="flex items-center gap-1.5 mb-1">
                    <Icon name={stat.icon} size={10} className="text-slate-400" />
                    <span className="text-[8px] font-black text-slate-400tracking-widest">{stat.label}</span>
                  </div>
                  <span className="text-sm font-black text-slate-900">{stat.value}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Behavior Issues */}
      {hasIssues && (
        <div className="bg-red-50 border border-red-100 rounded-[2rem] p-5">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-xl bg-red-500 text-white shadow-sm">
              <Icon name="AlertTriangle" size={16} />
            </div>
            <div>
              <h3 className="text-xs font-black text-red-700tracking-[0.15em]">Interview Issues Detected</h3>
              {behaviorFlags.scorePenalty > 0 && (
                <p className="text-[10px] text-red-500 font-bold mt-0.5">-{behaviorFlags.scorePenalty} points penalty applied</p>
              )}
            </div>
          </div>

          <div className="space-y-2.5">
            {behaviorFlags.earlyExit && (
              <div className="flex items-start gap-2.5 bg-white rounded-xl p-3 border border-red-100">
                <Icon name="LogOut" size={14} className="text-red-500 mt-0.5 shrink-0" />
                <div>
                  <h4 className="text-sm font-bold text-red-700">Early Exit</h4>
                  <p className="text-[11px] text-slate-600 font-medium">
                    Left at minute {Math.round(behaviorFlags.earlyExitMinute || 0)} of {Math.round(behaviorFlags.expectedDuration || 30)} expected.
                    Completing the full interview is essential for an accurate evaluation.
                  </p>
                </div>
              </div>
            )}

            {behaviorFlags.abusiveLanguage && (
              <div className="flex items-start gap-2.5 bg-white rounded-xl p-3 border border-red-100">
                <Icon name="XOctagon" size={14} className="text-red-500 mt-0.5 shrink-0" />
                <div>
                  <h4 className="text-sm font-bold text-red-700">Inappropriate Language</h4>
                  <p className="text-[11px] text-slate-600 font-medium">
                    Inappropriate language was detected. In a real interview, this would be a serious concern.
                  </p>
                </div>
              </div>
            )}

            {behaviorFlags.offTopicCount > 0 && (
              <div className="flex items-start gap-2.5 bg-white rounded-xl p-3 border border-red-100">
                <Icon name="Shuffle" size={14} className="text-amber-500 mt-0.5 shrink-0" />
                <div>
                  <h4 className="text-sm font-bold text-amber-700">Off-Topic Diversions ({behaviorFlags.offTopicCount})</h4>
                  <p className="text-[11px] text-slate-600 font-medium">
                    Stay focused on the interview questions. Going off-topic wastes valuable time.
                  </p>
                </div>
              </div>
            )}

            {behaviorFlags.tabSwitchDetected && (
              <div className="flex items-start gap-2.5 bg-white rounded-xl p-3 border border-red-100">
                <Icon name="PanelTopClose" size={14} className="text-red-500 mt-0.5 shrink-0" />
                <div>
                  <h4 className="text-sm font-bold text-red-700">Tab/Window Switching ({behaviorFlags.tabSwitchCount || 1})</h4>
                  <p className="text-[11px] text-slate-600 font-medium">
                    The interview tab lost focus during the session. This is treated as an integrity violation.
                  </p>
                </div>
              </div>
            )}

            {behaviorFlags.copyPasteDetected && (
              <div className="flex items-start gap-2.5 bg-white rounded-xl p-3 border border-red-100">
                <Icon name="ClipboardX" size={14} className="text-red-500 mt-0.5 shrink-0" />
                <div>
                  <h4 className="text-sm font-bold text-red-700">Copy/Paste Blocked</h4>
                  <p className="text-[11px] text-slate-600 font-medium">
                    Copy/cut attempts: {behaviorFlags.copyAttemptCount || 0}. Paste attempts: {behaviorFlags.pasteAttemptCount || 0}.
                  </p>
                </div>
              </div>
            )}

            {behaviorFlags.selectionAttemptCount > 0 && (
              <div className="flex items-start gap-2.5 bg-white rounded-xl p-3 border border-red-100">
                <Icon name="TextSelect" size={14} className="text-amber-500 mt-0.5 shrink-0" />
                <div>
                  <h4 className="text-sm font-bold text-amber-700">Question Selection Blocked ({behaviorFlags.selectionAttemptCount})</h4>
                  <p className="text-[11px] text-slate-600 font-medium">
                    The candidate attempted to select protected question text.
                  </p>
                </div>
              </div>
            )}

            {behaviorFlags.penaltyReasons && behaviorFlags.penaltyReasons.length > 0 && (
              <div className="bg-white rounded-xl p-3 border border-red-100">
                <p className="text-[9px] font-black text-red-500tracking-widest mb-1.5">Penalty Breakdown</p>
                <ul className="space-y-1">
                  {behaviorFlags.penaltyReasons.map((reason, i) => (
                    <li key={i} className="text-[11px] text-slate-600 font-medium flex items-center gap-1.5">
                      <div className="w-1 h-1 rounded-full bg-red-400 shrink-0" />
                      {reason}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default BehaviorFlagsSection;
